import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaUserShield, 
  FaCrown,
  FaRocket,
  FaEye, 
  FaEdit, 
  FaBan,
  FaUserPlus,
  FaFilter,
  FaProjectDiagram,
  FaBox,
  FaPuzzlePiece,
  FaLink,
  FaHeadset,
  FaTicketAlt,
  FaCreditCard,
  FaSpinner,
  FaTimes,
  FaAd,
  FaCalendarAlt
} from 'react-icons/fa';

const subscriptionPlans = [
  { value: 'free', label: 'Ücretsiz', color: 'bg-gray-100 text-gray-800', icon: FaUserShield },
  { value: 'premium', label: 'Premium', color: 'bg-blue-100 text-blue-800', icon: FaCrown },
  { value: 'enterprise', label: 'Kurumsal', color: 'bg-purple-100 text-purple-800', icon: FaRocket }
];

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterPlan, setFilterPlan] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let url = `/api/dashboard/admin/users?page=${currentPage}&limit=10`;
      if (filterPlan) url += `&plan=${filterPlan}`;
      if (searchTerm) url += `&search=${searchTerm}`;
      
      const usersRes = await fetch(url, { headers });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users);
        setTotalPages(usersData.totalPages);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filterPlan]);

  // Separate effect for search term to avoid infinite loops
  useEffect(() => {
    if (searchTerm) {
      const timeoutId = setTimeout(() => {
        fetchUsers();
      }, 500); // Debounce search
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlanIcon = (plan) => {
    const planData = subscriptionPlans.find(p => p.value === plan);
    return planData ? planData.icon : FaUserShield;
  };

  const getPlanColor = (plan) => {
    const planData = subscriptionPlans.find(p => p.value === plan);
    return planData ? planData.color : 'bg-gray-100 text-gray-800';
  };

  const getUserStatusColor = (isActive) => {
    return isActive ? 'text-green-400 bg-green-900/20 border-green-500/30' : 'text-red-400 bg-red-900/20 border-red-500/30';
  };

  const getEmailVerificationColor = (emailVerified) => {
    return emailVerified ? 'text-green-400 bg-green-900/20 border-green-500/30' : 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserEditModal(true);
  };

  const handleViewUserDetails = async (user) => {
    setSelectedUser(user);
    setLoadingDetails(true);
    setShowUserDetailModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/auth/users/${user._id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
      } else {
        console.error('Failed to fetch user details');
        setUserDetails(null);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setUserDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUpdateUserSubscription = async (userId, newPlan, isActive, emailVerified) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/auth/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription: {
            plan: newPlan,
            isActive: isActive
          },
          emailVerified: emailVerified
        })
      });
      
      if (response.ok) {
        const updatedUser = await response.json();
        console.log('Updated user:', updatedUser);
        
        // Update the user in the local state immediately
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user._id === userId 
              ? { ...user, ...updatedUser.user }
              : user
          )
        );
        
        setShowUserEditModal(false);
        setSelectedUser(null);
      } else {
        const errorData = await response.json();
        alert(`Güncelleme hatası: ${errorData.message}`);
      }
    } catch (error) {
      alert('Kullanıcı güncellenirken bir hata oluştu.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        {/* Filters */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Abonelik Planı</label>
              <select
                value={filterPlan}
                onChange={(e) => {
                  setFilterPlan(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tüm Planlar</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.value} value={plan.value}>
                    {plan.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterPlan('');
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="px-4 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors"
              >
                <FaFilter className="inline mr-2" />
                Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white">Kullanıcı Yönetimi</h3>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <FaUserPlus className="inline mr-2" />
              Yeni Kullanıcı
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">E-posta</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email Doğrulama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Abonelik</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Proje Sayısı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kayıt Tarihi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {users.map((user) => {
                  const PlanIcon = getPlanIcon(user.subscription?.plan || 'free');
                  return (
                    <tr key={user._id} className="hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-400">
                              {user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <div className="text-sm text-gray-300">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getEmailVerificationColor(user.emailVerified)}`}>
                          {user.emailVerified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <div className="flex items-center">
                          <PlanIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPlanColor(user.subscription?.plan || 'free')}`}>
                            {subscriptionPlans.find(p => p.value === (user.subscription?.plan || 'free'))?.label || 'Ücretsiz'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <div className="flex items-center">
                          <FaProjectDiagram className="h-4 w-4 text-blue-400 mr-2" />
                          <span className="text-sm text-gray-300">{user.projectCount || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-gray-400">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleEditUser(user)}
                            className="text-blue-400 hover:text-blue-300"
                            title="Düzenle"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleViewUserDetails(user)}
                            className="text-green-400 hover:text-green-300"
                            title="Detayları Görüntüle"
                          >
                            <FaEye className="h-4 w-4" />
                          </button>
                          <button 
                            className="text-red-400 hover:text-red-300"
                            title="Devre Dışı Bırak"
                          >
                            <FaBan className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Önceki
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Sayfa <span className="font-medium text-white">{currentPage}</span> /{' '}
                <span className="font-medium text-white">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Önceki
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {showUserEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 p-8 rounded-xl max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-white mb-4">Kullanıcı Düzenle</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Kullanıcı</label>
                <p className="text-white">{selectedUser.firstName} {selectedUser.lastName}</p>
                <p className="text-gray-400 text-sm">{selectedUser.email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Abonelik Planı</label>
                <select
                  value={selectedUser.subscription?.plan || 'free'}
                  onChange={(e) => setSelectedUser({
                    ...selectedUser,
                    subscription: { ...selectedUser.subscription, plan: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {subscriptionPlans.map((plan) => (
                    <option key={plan.value} value={plan.value}>
                      {plan.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Doğrulama</label>
                <select
                  value={selectedUser.emailVerified ? 'verified' : 'unverified'}
                  onChange={(e) => setSelectedUser({
                    ...selectedUser,
                    emailVerified: e.target.value === 'verified'
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="verified">Doğrulanmış</option>
                  <option value="unverified">Doğrulanmamış</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Durum</label>
                <select
                  value={selectedUser.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setSelectedUser({
                    ...selectedUser,
                    isActive: e.target.value === 'active'
                  })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUserEditModal(false)}
                className="px-4 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => handleUpdateUserSubscription(
                  selectedUser._id,
                  selectedUser.subscription?.plan || 'free',
                  selectedUser.isActive,
                  selectedUser.emailVerified
                )}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 p-6 rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Kullanıcı Detayları</h2>
              <button
                onClick={() => setShowUserDetailModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <FaSpinner className="animate-spin text-2xl text-blue-500 mr-3" />
                <span className="text-gray-300">Yükleniyor...</span>
              </div>
            ) : userDetails ? (
              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Kullanıcı Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Ad Soyad</label>
                      <p className="text-white">{userDetails.user.firstName} {userDetails.user.lastName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">E-posta</label>
                      <p className="text-white">{userDetails.user.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Rol</label>
                      <p className="text-white">{userDetails.user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Durum</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getUserStatusColor(userDetails.user.isActive)}`}>
                        {userDetails.user.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">E-posta Doğrulama</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getEmailVerificationColor(userDetails.user.emailVerified)}`}>
                        {userDetails.user.emailVerified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Kayıt Tarihi</label>
                      <p className="text-white">{formatDate(userDetails.user.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Kullanım İstatistikleri</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <FaProjectDiagram className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{userDetails.stats.totalProjects}</p>
                      <p className="text-sm text-gray-400">Proje</p>
                    </div>
                    <div className="text-center">
                      <FaBox className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{userDetails.stats.totalProducts}</p>
                      <p className="text-sm text-gray-400">Ürün</p>
                    </div>
                    <div className="text-center">
                      <FaLink className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{userDetails.stats.totalShortLinks}</p>
                      <p className="text-sm text-gray-400">Kısa Link</p>
                    </div>
                    <div className="text-center">
                      <FaHeadset className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{userDetails.stats.totalSupportModules}</p>
                      <p className="text-sm text-gray-400">Destek Modülü</p>
                    </div>
                    <div className="text-center">
                      <FaAd className="h-8 w-8 text-red-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{userDetails.stats.totalInternalAds || 0}</p>
                      <p className="text-sm text-gray-400">İç Reklam</p>
                    </div>
                  </div>
                </div>

                {/* Projects */}
                {userDetails.projects.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Projeler ({userDetails.projects.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.projects.map((project) => (
                        <div key={project._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{project.name}</p>
                          <p className="text-gray-300 text-sm">{project.description}</p>
                          <p className="text-gray-400 text-xs">{formatDate(project.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products */}
                {userDetails.products.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Ürünler ({userDetails.products.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.products.map((product) => (
                        <div key={product._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-gray-300 text-sm">{product.description}</p>
                          <p className="text-gray-400 text-xs">{formatDate(product.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Short Links */}
                {userDetails.shortLinks.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Kısa Linkler ({userDetails.shortLinks.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.shortLinks.map((link) => (
                        <div key={link._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{link.shortCode}</p>
                          <p className="text-gray-300 text-sm break-all">{link.originalUrl}</p>
                          <p className="text-gray-400 text-xs">{formatDate(link.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Support Modules */}
                {userDetails.supportModules.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Destek Modülleri ({userDetails.supportModules.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.supportModules.map((module) => (
                        <div key={module._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{module.title}</p>
                          <p className="text-gray-300 text-sm">{module.description}</p>
                          <p className="text-gray-400 text-xs">{formatDate(module.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Support Tickets */}
                {userDetails.supportTickets.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Destek Talepleri ({userDetails.supportTickets.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.supportTickets.map((ticket) => (
                        <div key={ticket._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{ticket.subject}</p>
                          <p className="text-gray-300 text-sm">{ticket.status}</p>
                          <p className="text-gray-400 text-xs">{formatDate(ticket.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Internal Ads */}
                {userDetails.internalAds && userDetails.internalAds.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">İç Reklamlar ({userDetails.internalAds.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.internalAds.map((ad) => (
                        <div key={ad._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{ad.title}</p>
                          <p className="text-gray-300 text-sm">{ad.description}</p>
                          <p className="text-gray-400 text-xs">{formatDate(ad.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payments */}
                {userDetails.payments.length > 0 && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Ödemeler ({userDetails.payments.length})</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userDetails.payments.map((payment) => (
                        <div key={payment._id} className="bg-gray-600 rounded p-3">
                          <p className="text-white font-medium">{payment.amount} {payment.currency}</p>
                          <p className="text-gray-300 text-sm">{payment.status}</p>
                          <p className="text-gray-400 text-xs">{formatDate(payment.createdAt)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400">Kullanıcı detayları yüklenemedi.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
